import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  InputBase,
  MenuItem,
  Select,
  Stack,
  useTheme,
} from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { TextMaskPhone } from 'src/components/Forms/FormPhoneInput/form-phone-input';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import {
  AddClientFormField,
  type AddClientFormValues,
  type ClientContactField,
  EMPTY_CONTACT_ROW,
  validateContactRow,
} from './add-client-form';

const LABEL_PLACEHOLDER = 'Label';

interface ClientContactRowsProps {
  name: ClientContactField;
  label: string;
  labelOptions?: { id: number; label: string }[];
  disabled?: boolean;
  testIdPrefix: string;
}

/** Email or phone rows under one label; each row has its own label, primary star and remove. */
export const ClientContactRows = ({
  name,
  label,
  labelOptions,
  disabled = false,
  testIdPrefix,
}: ClientContactRowsProps) => {
  const theme = useTheme();
  const { control, getValues, setValue } = useFormContext<AddClientFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name });
  const isPhone = name === AddClientFormField.PhoneNumbers;
  const lowerLabel = label.toLowerCase();

  // A setValue on the array itself would regenerate every row id and remount the rows.
  const setPrimary = (index: number, isPrimary: boolean) => {
    // react-hook-form types this value as `never` for a field-array row path (also with a concrete
    // `emails.${number}` path or a FieldPath annotation); the path itself is valid at runtime.
    setValue(`${name}.${index}.isPrimary`, isPrimary as never, { shouldDirty: true });
  };

  const makePrimary = (primaryIndex: number) => {
    getValues(name).forEach((_, index) => setPrimary(index, index === primaryIndex));
  };

  const removeRow = (index: number) => {
    const wasPrimary = getValues(`${name}.${index}.isPrimary`);
    remove(index);
    if (wasPrimary && getValues(name).length > 0) {
      makePrimary(0);
    }
  };

  const addRow = () => {
    append({ ...EMPTY_CONTACT_ROW, isPrimary: fields.length === 0 });
  };

  return (
    <Stack>
      <FormFieldLabel disabled={disabled}>{label}</FormFieldLabel>
      <Stack gap={1}>
        {fields.map((field, index) => (
          <FormTextField
            key={field.id}
            type="textfield"
            name={`${name}.${index}.value`}
            label=""
            disabled={disabled}
            showErrorText
            required={`Enter the ${lowerLabel} or remove the row`}
            rules={{ validate: validateContactRow(name, index), deps: [name] }}
            data-testid={`${testIdPrefix}-input-${index}`}
            textFieldProps={{
              placeholder: label,
              inputProps: { 'aria-label': `${label} ${index + 1}` },
              InputProps: {
                ...(isPhone && { inputComponent: TextMaskPhone, type: 'tel' }),
                endAdornment: (
                  <InputAdornment position="end">
                    <Controller
                      name={`${name}.${index}.labelId`}
                      control={control}
                      render={({ field: labelField }) => (
                        <Select
                          {...labelField}
                          input={
                            <InputBase sx={{ font: 'inherit', color: 'text.secondary' }} />
                          }
                          displayEmpty
                          disabled={disabled}
                          inputProps={{ 'aria-label': `${label} ${index + 1} label` }}
                          renderValue={(selected) =>
                            labelOptions?.find((option) => option.id === selected)?.label ??
                            LABEL_PLACEHOLDER
                          }
                        >
                          {labelOptions?.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    <Controller
                      name={`${name}.${index}.isPrimary`}
                      control={control}
                      render={({ field: primaryField }) => {
                        const star = (
                          <Checkbox
                            size="small"
                            checked={primaryField.value}
                            disabled={disabled || primaryField.value}
                            onChange={() => makePrimary(index)}
                            inputProps={{ 'aria-label': `Primary ${lowerLabel} ${index + 1}` }}
                            icon={<DynamicIcon name="star_outline" fontSize="1.25rem" />}
                            checkedIcon={
                              <DynamicIcon
                                name="star_outline"
                                filled
                                fontSize="1.25rem"
                                color={theme.palette.warning.main}
                              />
                            }
                          />
                        );
                        return primaryField.value ? (
                          star
                        ) : (
                          <Tooltip title={`Set as primary ${lowerLabel}`}>{star}</Tooltip>
                        );
                      }}
                    />
                    <IconButton
                      size="small"
                      edge="end"
                      disabled={disabled}
                      aria-label={`Remove ${lowerLabel} ${index + 1}`}
                      onClick={() => removeRow(index)}
                    >
                      <DynamicIcon name="x" library="lucide" size={16} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        ))}
      </Stack>
      <Button
        size="small"
        disabled={disabled}
        onClick={addRow}
        startIcon={<DynamicIcon name="plus" library="lucide" size={16} />}
        data-testid={`${testIdPrefix}-add`}
        sx={{ alignSelf: 'flex-start', mt: fields.length ? 1 : 0 }}
      >
        Add {lowerLabel}
      </Button>
    </Stack>
  );
};
